import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, SectionLabel, buttonVariants, Wordmark } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import {
  calendarCheckUrl,
  fetchCalendarCheckInfo,
  microsoftAdminConsentUrl,
  type CalendarCheckProviderInfo,
} from '@/lib/calendarCheck.ts';
import { INTEGRATION_PROVIDERS, type IntegrationProvider } from '@/lib/integrations.ts';

/*
 * The one page someone can be sent before they are a customer.
 *
 * It sits outside `_app` on purpose: there is no session, no account and no
 * cookie behind it, because the question it answers — may Wundo read a calendar
 * in this organisation — is the one an IT department wants settled *before*
 * anyone is rolled out. A forwarded link has to render for whoever opens it.
 */
export const Route = createFileRoute('/check/calendar')({
  component: CalendarCheckPage,
  validateSearch: (search: Record<string, unknown>) => ({
    provider: isProvider(search.provider) ? search.provider : undefined,
    status:
      search.status === 'ok' || search.status === 'error' ? search.status : undefined,
    code: typeof search.code === 'string' ? search.code : undefined,
    description: typeof search.description === 'string' ? search.description : undefined,
  }),
});

function isProvider(value: unknown): value is IntegrationProvider {
  return value === 'google' || value === 'microsoft';
}

/*
 * The one refusal that is about the account that was tested, not the tenant, so
 * neither the red verdict nor the tenant-wide remedy below it applies.
 */
const ACCOUNT_FAILURE_CODE = 'mailbox_not_enabled';

const PROVIDER_NAMES: Record<IntegrationProvider, () => string> = {
  google: m.check_calendar_provider_google,
  microsoft: m.check_calendar_provider_microsoft,
};

function CalendarCheckPage() {
  const { provider, status, code, description } = Route.useSearch();

  return (
    <main className='mx-auto flex min-h-dvh max-w-190 flex-col gap-6 px-6 py-12'>
      <div>
        <Wordmark registered className='mb-8 h-8' />
        <h1 className='text-xl font-semibold tracking-tight'>
          {m.check_calendar_title()}
        </h1>
      </div>

      {status === 'ok' && provider && <SuccessCard provider={provider} />}
      {status === 'error' && provider && (
        <FailureCard provider={provider} code={code} description={description} />
      )}

      <StartButtons repeat={status !== undefined} />

      {status === 'error' && code !== ACCOUNT_FAILURE_CODE && <WhitelistSection />}
    </main>
  );
}

/*
 * The two ways in, and the only controls on the page before a result.
 *
 * Links rather than buttons: this leaves the app entirely for the provider's
 * consent screen, and an administrator who wants to hand the test to a
 * colleague can copy the address out of one.
 */
function StartButtons({ repeat }: { repeat: boolean }) {
  return (
    <div className='flex flex-wrap gap-2.5'>
      {INTEGRATION_PROVIDERS.map((provider) => (
        <a
          key={provider}
          href={calendarCheckUrl(provider)}
          className={buttonVariants({
            variant: repeat ? 'quiet' : 'accent',
            shape: 'block',
            size: 'lg',
          })}
        >
          {repeat
            ? m.check_calendar_retry({ provider: PROVIDER_NAMES[provider]() })
            : m.check_calendar_start({ provider: PROVIDER_NAMES[provider]() })}
        </a>
      ))}
    </div>
  );
}

function SuccessCard({ provider }: { provider: IntegrationProvider }) {
  return (
    <Card tone='accent'>
      <h2 className='text-md font-semibold text-success'>
        {m.check_calendar_ok_title({ provider: PROVIDER_NAMES[provider]() })}
      </h2>
    </Card>
  );
}

/*
 * What the provider said, unedited.
 *
 * The audience is an IT administrator who will paste this into a ticket or a
 * search box, so the raw code and description are worth more than a translation
 * of them — and a code we have never seen still reaches the person who can act
 * on it. An account that has no mailbox is the exception: left unnamed it reads
 * as a verdict on the whole tenant.
 */
function FailureCard({
  provider,
  code,
  description,
}: {
  provider: IntegrationProvider;
  code?: string;
  description?: string;
}) {
  const accountFailure = code === ACCOUNT_FAILURE_CODE;

  return (
    <Card className='border-danger-line bg-danger-fill'>
      <h2 className='text-md font-semibold text-danger'>
        {accountFailure
          ? m.check_calendar_error_account_title({
              provider: PROVIDER_NAMES[provider](),
            })
          : m.check_calendar_error_title({
              provider: PROVIDER_NAMES[provider](),
            })}
      </h2>

      {accountFailure && (
        <p className='mt-2 text-base'>{m.check_calendar_error_account_hint()}</p>
      )}

      {(code ?? description) && (
        <div className='mt-4'>
          <SectionLabel as='h3'>{m.check_calendar_error_detail()}</SectionLabel>
          <pre className='mt-1.5 overflow-x-auto rounded-md bg-surface px-3.5 py-2.5 font-mono text-sm whitespace-pre-wrap'>
            {[code, description].filter(Boolean).join('\n')}
          </pre>
        </div>
      )}
    </Card>
  );
}

/*
 * Only shown once something failed: before that it is noise, and after it is
 * the whole remedy — the client to allow, the permission it asks for, and for
 * Entra the screen that grants it tenant-wide.
 */
function WhitelistSection() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['calendar-check-info'],
    queryFn: fetchCalendarCheckInfo,
  });

  if (isPending) return null;
  if (isError) {
    return (
      <p className='text-base text-ink-muted'>{m.check_calendar_whitelist_failed()}</p>
    );
  }

  return (
    <section className='flex flex-col gap-3'>
      <h2 className='text-md font-semibold'>{m.check_calendar_whitelist_title()}</h2>

      {data.map((info) => (
        <ProviderCard key={info.provider} info={info} />
      ))}
    </section>
  );
}

function ProviderCard({ info }: { info: CalendarCheckProviderInfo }) {
  return (
    <Card>
      <h3 className='text-md font-semibold'>{PROVIDER_NAMES[info.provider]()}</h3>

      <dl className='mt-3 flex flex-col gap-3'>
        <CopyableValue label={m.check_calendar_client_id()} value={info.client_id} />
        <CopyableValue label={m.check_calendar_scope()} value={info.scope} />
      </dl>

      {info.provider === 'microsoft' && (
        <a
          href={microsoftAdminConsentUrl(info.client_id)}
          className={buttonVariants({
            variant: 'quiet',
            shape: 'block',
            size: 'sm',
            className: 'mt-4',
          })}
        >
          {m.check_calendar_admin_consent()}
        </a>
      )}
    </Card>
  );
}

/* Both values get pasted into a console somewhere, so both are worth one tap. */
function CopyableValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className='flex flex-wrap items-center gap-2'>
      <dt className='w-32 shrink-0 text-sm font-semibold text-ink-secondary'>{label}</dt>
      <dd className='min-w-0 flex-1 overflow-x-auto font-mono text-sm'>{value}</dd>
      <Button
        size='sm'
        variant='ghost'
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => setCopied(true));
        }}
      >
        {copied ? m.check_calendar_copied() : m.check_calendar_copy()}
      </Button>
    </div>
  );
}
